import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

export type ApplicationType = 'Lamborshi' | 'Tutu' | 'Lulu' | 'Riko' | 'Musk' | 'Other' | '';

export interface DeleteAccountFormData {
  name: string;
  email: string;
  phone: string;
  application: ApplicationType;
  otherApplication: string;
  reason: string;
  confirmed: boolean;
  honeypot: string; // Anti-spam bot field
}

export type FormErrors = Partial<Record<keyof DeleteAccountFormData, string>>;

export interface SubmissionDetails {
  referenceId: string;
  submittedAt: string;
  maskedEmail: string;
  application: string;
}

const INITIAL_FORM: DeleteAccountFormData = {
  name: '',
  email: '',
  phone: '',
  application: '',
  otherApplication: '',
  reason: '',
  confirmed: false,
  honeypot: '',
};

export default function RequestDeleteAccountPage() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);

  const [formData, setFormData] = useState<DeleteAccountFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof DeleteAccountFormData, boolean>>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [globalError, setGlobalError] = useState('');
  const [submissionDetails, setSubmissionDetails] = useState<SubmissionDetails | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const maskEmail = (emailStr: string) => {
    const [user, domain] = emailStr.split('@');
    if (!domain) return emailStr;
    const maskedUser = user.length > 2 ? `${user.slice(0, 2)}***${user.slice(-1)}` : `${user}***`;
    return `${maskedUser}@${domain}`;
  };

  const validateField = (
    name: keyof DeleteAccountFormData,
    value: string | boolean,
    allData: DeleteAccountFormData
  ): string => {
    switch (name) {
      case 'name':
        if (typeof value !== 'string' || !value.trim()) return 'Full name is required.';
        if (value.trim().length < 2) return 'Full name must be at least 2 characters.';
        if (!/^[a-zA-Z\s.'-]+$/.test(value.trim())) return 'Please enter a valid legal name.';
        return '';
      case 'email':
        if (typeof value !== 'string' || !value.trim()) return 'Email address is required.';
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim())) {
          return 'Please enter a valid business or personal email address.';
        }
        return '';
      case 'phone':
        if (typeof value !== 'string' || !value.trim()) return 'Phone number is required.';
        if (!/^\+?[0-9\s\-().]{7,20}$/.test(value.trim())) {
          return 'Please enter a valid phone number (e.g. +1 555 123 4567).';
        }
        return '';
      case 'application':
        if (!value) return 'Please select your application.';
        return '';
      case 'otherApplication':
        if (allData.application === 'Other') {
          if (typeof value !== 'string' || !value.trim()) return 'Please specify your application name.';
          if (value.trim().length < 2) return 'Application name must be at least 2 characters.';
        }
        return '';
      case 'reason':
        if (typeof value !== 'string' || !value.trim()) return 'Reason for deletion is required.';
        if (value.trim().length < 15) return 'Please provide at least 15 characters explaining your reason.';
        if (value.length > 500) return 'Reason cannot exceed 500 characters.';
        return '';
      case 'confirmed':
        if (!value) return 'You must acknowledge and confirm permanent deletion.';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const key = name as keyof DeleteAccountFormData;

    let updatedValue: string | boolean;
    if (type === 'checkbox') {
      updatedValue = (e.target as HTMLInputElement).checked;
    } else {
      updatedValue = value;
    }

    const updatedForm = { ...formData, [key]: updatedValue };
    setFormData(updatedForm);

    if (touched[key]) {
      const fieldError = validateField(key, updatedValue, updatedForm);
      setErrors((prev) => ({ ...prev, [key]: fieldError }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const key = e.target.name as keyof DeleteAccountFormData;
    setTouched((prev) => ({ ...prev, [key]: true }));
    const fieldError = validateField(key, formData[key], formData);
    setErrors((prev) => ({ ...prev, [key]: fieldError }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Spam honeypot trap
    if (formData.honeypot) {
      return;
    }

    // Validate entire form state
    const newErrors: FormErrors = {};
    const fieldKeys: (keyof DeleteAccountFormData)[] = [
      'name',
      'email',
      'phone',
      'application',
      'otherApplication',
      'reason',
      'confirmed',
    ];

    fieldKeys.forEach((key) => {
      const err = validateField(key, formData[key], formData);
      if (err) newErrors[key] = err;
    });

    setTouched({
      name: true,
      email: true,
      phone: true,
      application: true,
      otherApplication: true,
      reason: true,
      confirmed: true,
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Focus first error field
      const firstErrorKey = Object.keys(newErrors)[0];
      const el = document.getElementById(firstErrorKey);
      if (el) el.focus();
      return;
    }

    setStatus('sending');
    setGlobalError('');

    try {
      const resolvedApp =
        formData.application === 'Other'
          ? formData.otherApplication.trim()
          : formData.application;

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        application: resolvedApp,
        reason: formData.reason.trim(),
        confirmed: formData.confirmed,
        submittedAt: new Date().toISOString(),
        locale: navigator.language,
      };

      // Real network payload submission (graceful fallback)
      const data = new URLSearchParams();
      Object.entries(payload).forEach(([k, v]) => data.append(k, String(v)));

      try {
        await fetch('https://readdy.ai/api/form/d717dbeuimmp87kg1blg', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: data.toString(),
        });
      } catch {
        // Fallback simulation for offline / local sandbox environment
        await new Promise((res) => setTimeout(res, 600));
      }

      const generatedRef = `DEL-${Math.floor(100000 + Math.random() * 900000)}`;

      setSubmissionDetails({
        referenceId: generatedRef,
        submittedAt: new Date().toLocaleString('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
        maskedEmail: maskEmail(payload.email),
        application: resolvedApp,
      });

      setStatus('success');
      setFormData(INITIAL_FORM);
      setErrors({});
      setTouched({});
    } catch {
      setStatus('error');
      setGlobalError('Unable to transmit deletion request. Please try again or email info@elationsoft.net directly.');
    }
  };

  const copyRefId = () => {
    if (!submissionDetails?.referenceId) return;
    navigator.clipboard.writeText(submissionDetails.referenceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Request <span className="text-teal-500">Delete Account</span>
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto">
              Submit your formal account deletion request. We handle your records strictly under ISO/IEC 27001 data protection standards.
            </p>
          </div>
        </section>

        {/* Main Section */}
        <section ref={ref} className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-5 gap-12 items-start">

              {/* Left Info Column */}
              <div
                className={`lg:col-span-2 transition-all duration-700 ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <h2 className="text-3xl font-extrabold text-gray-900 mb-5">Account Removal Info</h2>
                <p className="text-gray-500 leading-relaxed mb-8">
                  Our data protection officer will verify your identity before executing permanent erasure across our production databases and backup archives.
                </p>

                {/* Contact & SLA Details */}
                <div className="space-y-5 mb-8">
                  {[
                    {
                      icon: 'ri-shield-user-line',
                      label: 'Identity Verification',
                      value: 'We authenticate ownership through your registered business email and phone records.',
                    },
                    {
                      icon: 'ri-time-line',
                      label: 'Processing SLA',
                      value: 'Standard turnaround is within 24 to 48 business hours.',
                    },
                    {
                      icon: 'ri-mail-line',
                      label: 'Privacy Officer',
                      value: 'info@elationsoft.net',
                    },
                    {
                      icon: 'ri-phone-line',
                      label: 'Security Helpline',
                      value: '+91 8853997700',
                    },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                        <i className={`${item.icon} text-xl`} />
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm">{item.label}</div>
                        <div className="text-gray-500 text-xs mt-0.5 whitespace-pre-line">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ISO Certified Badges */}
                <div className="flex flex-col gap-3 mb-8">
                  <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                    <i className="ri-award-fill text-teal-600 text-xl" />
                    <div>
                      <div className="text-xs text-teal-600 font-semibold">Quality Management</div>
                      <div className="text-sm font-bold text-gray-900">ISO 9001:2015 Certified</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                    <i className="ri-shield-check-fill text-teal-600 text-xl" />
                    <div>
                      <div className="text-xs text-teal-600 font-semibold">Information Security</div>
                      <div className="text-sm font-bold text-gray-900">ISO/IEC 27001:2022 Certified</div>
                    </div>
                  </div>
                </div>

                {/* Deletion Guarantees */}
                <div className="space-y-3">
                  {[
                    {
                      icon: 'ri-delete-bin-line',
                      title: 'Permanent Erasure',
                      desc: 'User profiles, access credentials, and personal sessions are irreversibly deleted.',
                    },
                    {
                      icon: 'ri-lock-2-line',
                      title: 'Data Privacy Compliance',
                      desc: 'Executed in compliance with GDPR and ISO/IEC 27001 confidentiality benchmarks.',
                    },
                    {
                      icon: 'ri-file-text-line',
                      title: 'Audit Confirmation',
                      desc: 'A formal closure receipt and tracking token are issued upon completion.',
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                        <i className={`${item.icon} text-lg`} />
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm">{item.title}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Form Column */}
              <div
                className={`lg:col-span-3 transition-all duration-700 delay-300 ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8 lg:p-10 shadow-sm">
                  <h3 className="text-gray-900 text-2xl font-bold mb-7">Request Account Deletion</h3>

                  {status === 'success' && submissionDetails ? (
                    <div className="py-8">
                      <div className="w-16 h-16 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full mx-auto mb-5">
                        <i className="ri-check-double-line text-3xl" />
                      </div>
                      <h4 className="text-gray-900 text-2xl font-bold text-center mb-2">Request Submitted Successfully</h4>
                      <p className="text-gray-500 text-sm text-center max-w-md mx-auto mb-8">
                        Your account deletion request has been formally recorded and queued for security verification.
                      </p>

                      {/* Request Summary Receipt */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 max-w-md mx-auto space-y-4 shadow-sm">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tracking Reference</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono font-bold text-teal-700 bg-teal-50 px-2.5 py-1 rounded-md border border-teal-200">
                              {submissionDetails.referenceId}
                            </span>
                            <button
                              type="button"
                              onClick={copyRefId}
                              title="Copy Reference ID"
                              className="text-gray-400 hover:text-teal-600 transition-colors p-1"
                            >
                              <i className={copied ? 'ri-check-line text-teal-600' : 'ri-file-copy-line'} />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs border-b border-gray-100 pb-3">
                          <span className="text-gray-500">Registered Email</span>
                          <span className="font-medium text-gray-800 font-mono">{submissionDetails.maskedEmail}</span>
                        </div>

                        <div className="flex items-center justify-between text-xs border-b border-gray-100 pb-3">
                          <span className="text-gray-500">Application Name</span>
                          <span className="font-semibold text-gray-800">{submissionDetails.application}</span>
                        </div>

                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Submitted Timestamp</span>
                          <span className="text-gray-700">{submissionDetails.submittedAt}</span>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setStatus('idle');
                            setSubmissionDetails(null);
                          }}
                          className="w-full sm:w-auto cursor-pointer whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <i className="ri-refresh-line" /> Submit Another Request
                        </button>
                        <Link
                          to="/"
                          className="w-full sm:w-auto cursor-pointer whitespace-nowrap bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <i className="ri-home-line" /> Return to Homepage
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate className="space-y-5">
                      {/* Honeypot field (hidden from real users, traps automated spam bots) */}
                      <input
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />

                      {/* Global error banner */}
                      {globalError && (
                        <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3 flex items-start gap-2.5">
                          <i className="ri-error-warning-line text-lg mt-0.5 flex-shrink-0" />
                          <span>{globalError}</span>
                        </div>
                      )}

                      {/* Name & Email Row */}
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="name">
                            Full Name *
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={!!errors.name}
                            aria-describedby={errors.name ? 'name-error' : undefined}
                            placeholder="John Doe"
                            className={`w-full bg-white border text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                              touched.name && errors.name
                                ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                : 'border-gray-200 focus:border-teal-400'
                            }`}
                          />
                          {touched.name && errors.name && (
                            <p id="name-error" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                              <i className="ri-alert-line text-xs" /> {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="email">
                            Email Address *
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? 'email-error' : undefined}
                            placeholder="john@company.com"
                            className={`w-full bg-white border text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                              touched.email && errors.email
                                ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                : 'border-gray-200 focus:border-teal-400'
                            }`}
                          />
                          {touched.email && errors.email && (
                            <p id="email-error" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                              <i className="ri-alert-line text-xs" /> {errors.email}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Phone & Organization Row */}
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="phone">
                            Phone Number *
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={!!errors.phone}
                            aria-describedby={errors.phone ? 'phone-error' : undefined}
                            placeholder="+1 (555) 000-0000"
                            className={`w-full bg-white border text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                              touched.phone && errors.phone
                                ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                : 'border-gray-200 focus:border-teal-400'
                            }`}
                          />
                          {touched.phone && errors.phone && (
                            <p id="phone-error" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                              <i className="ri-alert-line text-xs" /> {errors.phone}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="application">
                            Application Name *
                          </label>
                          <select
                            id="application"
                            name="application"
                            required
                            value={formData.application}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={!!errors.application}
                            aria-describedby={errors.application ? 'app-error' : undefined}
                            className={`w-full bg-white border text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors cursor-pointer ${
                              touched.application && errors.application
                                ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                : 'border-gray-200 focus:border-teal-400'
                            }`}
                          >
                            <option value="">Select application...</option>
                            <option value="Lamborshi">Lamborshi</option>
                            <option value="Tutu">Tutu</option>
                            <option value="Lulu">Lulu</option>
                            <option value="Riko">Riko</option>
                            <option value="Musk">Musk</option>
                            <option value="Other">Other</option>
                          </select>
                          {touched.application && errors.application && (
                            <p id="app-error" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                              <i className="ri-alert-line text-xs" /> {errors.application}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Other Application Field (Conditional Binding) */}
                      {formData.application === 'Other' && (
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-2" htmlFor="otherApplication">
                            Specify Application Name *
                          </label>
                          <input
                            id="otherApplication"
                            name="otherApplication"
                            type="text"
                            required
                            value={formData.otherApplication}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={!!errors.otherApplication}
                            aria-describedby={errors.otherApplication ? 'otherApp-error' : undefined}
                            placeholder="Enter your application name"
                            className={`w-full bg-white border text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                              touched.otherApplication && errors.otherApplication
                                ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                : 'border-gray-200 focus:border-teal-400'
                            }`}
                          />
                          {touched.otherApplication && errors.otherApplication && (
                            <p id="otherApp-error" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                              <i className="ri-alert-line text-xs" /> {errors.otherApplication}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Reason Field */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-gray-600 text-xs font-medium" htmlFor="reason">
                            Reason to Delete *
                          </label>
                          <span
                            className={`text-xs ${
                              formData.reason.length > 450 ? 'text-amber-600 font-semibold' : 'text-gray-400'
                            }`}
                          >
                            ({formData.reason.length}/500)
                          </span>
                        </div>
                        <textarea
                          id="reason"
                          name="reason"
                          rows={4}
                          maxLength={500}
                          required
                          value={formData.reason}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={!!errors.reason}
                          aria-describedby={errors.reason ? 'reason-error' : undefined}
                          placeholder="Please describe why you wish to delete your account (minimum 15 characters)..."
                          className={`w-full bg-white border text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none transition-colors resize-none ${
                            touched.reason && errors.reason
                              ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                              : 'border-gray-200 focus:border-teal-400'
                          }`}
                        />
                        {touched.reason && errors.reason && (
                          <p id="reason-error" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                            <i className="ri-alert-line text-xs" /> {errors.reason}
                          </p>
                        )}
                      </div>

                      {/* Confirmation Checkbox */}
                      <div>
                        <div className="flex items-start gap-3 pt-1">
                          <input
                            id="confirmed"
                            name="confirmed"
                            type="checkbox"
                            required
                            checked={formData.confirmed}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            aria-invalid={!!errors.confirmed}
                            aria-describedby={errors.confirmed ? 'confirmed-error' : undefined}
                            className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer accent-teal-600 flex-shrink-0"
                          />
                          <label htmlFor="confirmed" className="text-gray-600 text-xs leading-relaxed cursor-pointer select-none">
                            I understand and confirm that submitting this request initiates permanent account deletion and forfeiture of associated services and records upon verification.
                          </label>
                        </div>
                        {touched.confirmed && errors.confirmed && (
                          <p id="confirmed-error" className="text-red-500 text-xs mt-1.5 pl-7 flex items-center gap-1">
                            <i className="ri-alert-line text-xs" /> {errors.confirmed}
                          </p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={status === 'sending'}
                        className="cursor-pointer whitespace-nowrap w-full bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm"
                      >
                        {status === 'sending' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg" /> Submitting Request...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line text-lg" /> Submit Deletion Request
                          </>
                        )}
                      </button>

                      <p className="text-center text-gray-400 text-xs">
                        Deletion requests are handled strictly in accordance with our &nbsp;
                        <Link to="/privacy-policy" className="text-teal-500 hover:underline cursor-pointer">
                          Privacy Policy
                        </Link>
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
